import { Box, Radio, styled, TextField } from '@mui/material';
import { ReactComponent as BackIcon } from 'core/assets/svg/common/back-icon.svg';
import BaseTooltip from 'core/components/tooltip/BaseTooltip';
import BaseTypography from 'core/components/typography/BaseTypography';
import { FontsEnum } from 'core/constants/enums/FontsEnum.ts';
import { colorPalette } from 'core/layouts/ui-blocks/ColorPalette';
import { reusableStyles } from 'core/layouts/ui-blocks/ReusableStyles';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export function BackButton() {
	const navigate = useNavigate();

	return (
		<Box
			id="back-button"
			sx={[reusableStyles.backButton]}
			onClick={(e) => {
				e.stopPropagation();
				navigate(-1);
			}}
		>
			<BackIcon />
		</Box>
	);
}

export function TabsContainer({ id, children = null, styles }) {
	return (
		<Box id={`${id}-tabs-cont`} sx={[reusableStyles.tabsCont, styles]}>
			{children}
		</Box>
	);
}

export function Tab({
	id,
	isSelected,
	icon = null,
	label,
	onClickFunc,
	fullWidth = false,
	style,
	textStyles,
	tooltipTitle,
}) {
	return (
		<BaseTooltip title={tooltipTitle} placement="right" arrow>
			<Box
				key={id}
				id={id}
				sx={[
					reusableStyles.tab,
					{
						borderColor: isSelected ? 'colors.systemGreen1' : 'transparent',
						width: fullWidth ? '100%' : 'auto',
						'& svg': {
							fill: isSelected ? `${colorPalette.light2}` : `${colorPalette.light4}`,
						},
					},
					style,
				]}
				onClick={onClickFunc}
			>
				{icon && icon}
				<BaseTypography
					variant="b1_medium"
					styles={[
						{
							typography: isSelected ? 'b1_bold' : 'b1_medium',
							color: isSelected ? 'colors.light3' : 'colors.light4',
						},
						textStyles,
					]}
				>
					{label}
				</BaseTypography>
			</Box>
		</BaseTooltip>
	);
}

export function CustomRadio({ ...props }) {
	return (
		<Radio
			// eslint-disable-next-line react/jsx-props-no-spreading
			{...props}
			sx={[reusableStyles.radio, ...(Array.isArray(props.sx) ? props.sx : [props.sx])]}
		/>
	);
}

export function CanvasRadio({ ...props }) {
	return (
		<Radio
			// eslint-disable-next-line react/jsx-props-no-spreading
			{...props}
			sx={[reusableStyles.canvasRadio]}
		/>
	);
}

export const CustomTextField = styled(TextField)({
	'& .MuiOutlinedInput-root': {
		'& fieldset': {
			borderColor: '#383838',
		},
		'&:hover fieldset': {
			borderColor: '#383838',
		},
		'&.Mui-focused fieldset': {
			borderColor: '#383838',
		},
	},
	'& .MuiInputBase-input': {
		fontFamily: FontsEnum.INTER,
		fontSize: '14px',
		fontWeight: 400,
		lineHeight: '24px',
		letterSpacing: '0.1px',
		color: '#383838',
	},
});

export function ConditionalLayer({ isShown, children }) {
	if (isShown) {
		// eslint-disable-next-line react/jsx-no-useless-fragment
		return <>{children}</>;
	}
	return null;
}

export function FreeTag({ sx }) {
	return (
		<Box
			sx={{
				padding: '4px 8px',
				width: 'min-content',
				backgroundColor: 'colors.tonal4',
				borderRadius: '4px',
				...sx,
			}}
		>
			<BaseTypography variant="b2_medium" color="colors.light1">
				Free
			</BaseTypography>
		</Box>
	);
}

export function LazyRenderLayer({ isShown, children }) {
	const [isRendered, setIsRendered] = useState(false);
	useEffect(() => {
		if (isShown) {
			setIsRendered(true);
		}
	}, [isShown]);
	if (!isRendered) {
		return null;
	}
	return (
		<Box
			sx={{
				display: isShown ? 'contents' : 'none',
			}}
		>
			{children}
		</Box>
	);
}
